import { Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * A round avatar carrying someone's initials.
 *
 * There are no photographs in this system and there is no reason to add a
 * place to upload one, but a list of a dozen names is read by shape rather
 * than by reading, and a coloured disc at the start of the row gives each
 * person a shape.
 *
 * The colour is **derived from a key**, not stored and not random: the same
 * person is the same colour on every screen and after every reload, which is
 * the only thing that makes it useful for recognition. The key should be
 * something that does not change — an id, not a name somebody may correct.
 *
 * Hue comes from the key, while saturation and lightness are fixed per theme,
 * so every disc is as light as every other and none of them shouts. The panel
 * is deliberately restrained — graphite structure, red used sparingly (see
 * `theme.ts`) — and a row of fully saturated circles would undo that.
 */
export function InitialsAvatar({
  name,
  colorKey,
  size = 38,
}: {
  /** Shown as initials; the first letter of each of the first two words. */
  name: string;
  /** What the colour is derived from. Defaults to the name. */
  colorKey?: string;
  size?: number;
}) {
  const theme = useTheme();
  const hue = hueOf(colorKey ?? name);
  const dark = theme.palette.mode === 'dark';

  return (
    <Avatar
      // Not an image, so it is decorative: the name is in the cell beside it
      // and a screen reader should not hear it twice.
      aria-hidden
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.37,
        fontWeight: 700,
        letterSpacing: '0.02em',
        bgcolor: dark ? `hsl(${hue} 38% 24%)` : `hsl(${hue} 62% 92%)`,
        color: dark ? `hsl(${hue} 62% 78%)` : `hsl(${hue} 46% 30%)`,
      }}
    >
      {initialsOf(name)}
    </Avatar>
  );
}

/** "Sofia Andersen" becomes "SA"; one word gives one letter. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toLocaleUpperCase() ?? '')
    .join('');
}

/**
 * A stable hue from a string.
 *
 * FNV-1a, because it is four lines and spreads short, similar strings — which
 * is what ids and usernames are — far better than summing character codes,
 * where "ali1" and "ali2" land next to each other and two people end up the
 * same colour.
 *
 * The twelve steps of 29 degrees are deliberate: adjacent keys land a long way
 * apart on the wheel, and nothing lands on a hue so close to the brand red
 * that an avatar could be mistaken for a status.
 */
function hueOf(key: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return ((hash % 12) * 29 + 18) % 360;
}
